import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star } from "lucide-react";
import { format } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";

interface Review {
  id: string;
  reviewer: {
    displayName: string;
    profilePhotoUrl?: string;
  };
  star_rating: string; // "ONE", "TWO", "THREE", "FOUR", "FIVE"
  comment?: string;
  create_time: string;
  update_time?: string;
  rating?: number; // Computed numeric rating
}

interface LandingReviewsSectionProps {
  content: any;
  theme: any;
  businessInfo?: any;
  location?: any;
  isStandalonePage?: boolean;
  ssrReviewsData?: any[] | null;
}

const LandingReviewsSection = ({ content, theme, location, isStandalonePage = false, ssrReviewsData }: LandingReviewsSectionProps) => {
  const [reviews, setReviews] = useState<Review[]>(ssrReviewsData || []);
  const [loading, setLoading] = useState(!ssrReviewsData);
  const navigate = useNavigate();
  const { subdomain } = useParams();

  // Check if we're on an actual subdomain (subdomain.localhost) vs testing route (/subdomain/:subdomain)
  const isOnSubdomain = () => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      // subdomain.localhost or subdomain.my-domain.com
      return parts.length >= 2 && parts[0] !== 'localhost' && parts[0] !== 'www';
    }
    return false;
  };

  const getBasePath = () => {
    return isOnSubdomain() ? '' : `/subdomain/${subdomain}`;
  };

  useEffect(() => {
    // If we have SSR data, don't fetch again
    if (ssrReviewsData) {
      return;
    }

    if (location?.id) {
      fetchReviews();
    }
  }, [location?.id, isStandalonePage, ssrReviewsData]);

  const fetchReviews = async () => {
    try {
      let locationId = location?.id;
      const limit = isStandalonePage ? 50 : 6;

      const { data, error } = await supabase
        .from("gmb_reviews")
        .select("*")
        .eq("location_id", locationId)
        .not("comment", "is", null)
        .neq("comment", "")
        .in("star_rating", ["FOUR", "FIVE"])
        .order("create_time", { ascending: false })
        .limit(limit);

      if (error) {
        console.error("Error fetching reviews:", error);
        return;
      }

      setReviews(data || []);
    } catch (err) {
      console.error("Error fetching reviews:", err);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-5 w-5 ${
          i < rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
        }`}
      />
    ));
  };

  const getInitials = (name: string) => {
    if (!name || typeof name !== 'string') {
      return '??';
    }
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  if (loading) {
    return (
      <section id="reviews" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="section-heading mb-4">
              Trusted by Our Community
            </h2>
            <div className="section-divider mb-4" />
            <p className="text-xl text-gray-600">Loading reviews...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!reviews || reviews.length === 0) {
    return null; // Don't show section if no reviews
  }

  // Convert string ratings to numbers and calculate average
  const getNumericRating = (starRating: string): number => {
    const ratingMap: { [key: string]: number } = {
      "ONE": 1,
      "TWO": 2,
      "THREE": 3,
      "FOUR": 4,
      "FIVE": 5
    };
    return ratingMap[starRating] || 0;
  };

  // Filter to only show reviews with 4+ stars, non-empty comments, and add numeric ratings
  const numericReviews = reviews
    .map(review => ({
      ...review,
      rating: getNumericRating(review.star_rating)
    }))
    .filter(review => review.rating >= 4 && review.comment && review.comment.trim() !== '');

  return (
    <section id="reviews" className="py-16 lg:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="section-heading mb-4">
            {content?.heading || "Trusted by Our Community"}
          </h2>
          <div className="section-divider" />
          <p className="section-description mt-6">
            {content?.description || "Join hundreds of satisfied customers who trust us with their needs. Here's what they have to say."}
          </p>
        </div>


        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {numericReviews.map((review) => (
            <Card key={review.id} className="review-card modern-card p-6 relative overflow-hidden border-0">
              {/* Decorative quote mark */}
              <div className="quote-mark">"</div>
              <div className="flex items-start gap-4 mb-4">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center font-semibold text-white flex-shrink-0 ring-4"
                  style={{ backgroundColor: "var(--primary)", ringColor: "color-mix(in srgb, var(--primary) 20%, transparent)" }}
                >
                  {review.reviewer?.profilePhotoUrl ? (
                    <img
                      src={review.reviewer.profilePhotoUrl}
                      alt={review.reviewer?.displayName || 'Reviewer'}
                      className="w-full h-full rounded-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        if (e.currentTarget.parentElement) {
                          e.currentTarget.parentElement.textContent = getInitials(review.reviewer?.displayName || '');
                        }
                      }}
                    />
                  ) : (
                    getInitials(review.reviewer?.displayName || '')
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold">{review.reviewer?.displayName || 'Anonymous'}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex">{renderStars(review.rating)}</div>
                    <span className="text-sm text-gray-500">
                      {format(new Date(review.create_time), "dd MMM yy")}
                    </span>
                  </div>
                </div>
              </div>
              {review.comment && (
                <p className="text-gray-600">{review.comment}</p>
              )}
            </Card>
          ))}
        </div>

        {!isStandalonePage && (
          <div className="text-center mt-12">
            <Button
              onClick={() => navigate(`${getBasePath()}/reviews`)}
              style={{ backgroundColor: "var(--primary)", color: "var(--primary-foreground)" }}
              className="hover:opacity-90"
            >
              Show More Reviews
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default LandingReviewsSection;